import { PaymentMethodTypes } from '@accedo/one-plugin-sdk';
import { FC, useMemo, useState } from 'react';

import {
  PaymentMethodFormProps,
  PaymentProps,
  TabPanelProps,
} from '#/components/Payment/types';
import { useConfig } from '#/hooks/useConfig';
import { noop } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import * as Styled from './Payment.styled';
import { PaymentCardForm } from './PaymentCardForm';
import { PaymentDropInForm } from './PaymentDropInForm';
import { PaymentPayPalForm } from './PaymentPayPalForm';

interface TypeProps {
  name: string;
  icon: string;
}

const types: Partial<Record<PaymentMethodTypes, TypeProps>> = {
  [PaymentMethodTypes.CreditCard]: {
    name: 'web.iap.payment.tab.credit',
    icon: 'images/payment-card.svg',
  },
  [PaymentMethodTypes.Paypal]: {
    name: 'web.iap.payment.tab.paypal',
    icon: 'images/payment-paypal.png',
  },
};

export const Payment = ({
  paymentMethods: unfilteredPaymentMethods,
  hasOwnUi,
  isPaymentDetailsRequired,
  buyButtonText,
  disclaimerText,
  onPurchase,
  onSuccess,
  onFailure,
  isDisabled,
  setIsDisabled = noop,
}: PaymentProps) => {
  // filter out unknown payment methods
  const paymentMethods = useMemo(
    () => unfilteredPaymentMethods.filter(({ type }) => type !== 'unknown'),
    [unfilteredPaymentMethods]
  );

  // first of the remaining payment methods (if any) will be selected by default
  const [activeTabType, setActiveTabType] = useState<
    PaymentMethodTypes | undefined
  >(paymentMethods[0]?.type);

  const hasMultiplePaymentMethods = paymentMethods.length > 1;

  const {
    app: { staticFilePrefix },
  } = useConfig();

  const handleTabClick = (type: PaymentMethodTypes) => () => {
    setActiveTabType(type);
  };

  if (!paymentMethods.length) {
    // Some of the plugins don't return payment methods. It was fixed in the latest versions, but some Customers are
    // still using older versions, especially pre-3DS versions of Cleeng. So we have to allow empty payment methods,
    // and fall back to credit card.
    // After all customers have been upgraded to the latest plugins, we can remove this fallback and revert to the
    // original strict error handling:
    //   onFailure(new Error('No available payment method!'));
    //   return null;
    paymentMethods.push({ type: PaymentMethodTypes.CreditCard });
  }

  return (
    <Styled.PaymentContainer data-testid="payment-container">
      {hasMultiplePaymentMethods && (
        <Styled.Tabs>
          {paymentMethods.map(({ type }) => (
            <Styled.Tab
              $selected={activeTabType === type}
              aria-controls={`payment-tabpanel-${type}`}
              disabled={isDisabled}
              id={`payment-tab-${type}`}
              key={type}
              onClick={handleTabClick(type)}
            >
              <Styled.Icon src={`${staticFilePrefix}${types[type]?.icon}`} />
              {tb(types[type]?.name)}
            </Styled.Tab>
          ))}
        </Styled.Tabs>
      )}

      {paymentMethods.map((paymentMethod) => {
        const { type } = paymentMethod;

        return (
          <TabPanel
            hasMultiplePaymentMethods={hasMultiplePaymentMethods}
            isSelected={activeTabType === type}
            key={type}
            type={type}
          >
            <PaymentMethodForm
              buyButtonText={buyButtonText}
              disclaimerText={disclaimerText}
              hasOwnUi={hasOwnUi}
              isPaymentDetailsRequired={isPaymentDetailsRequired}
              onFailure={onFailure}
              onPurchase={onPurchase}
              onSuccess={onSuccess}
              paymentMethod={paymentMethod}
              setIsDisabled={setIsDisabled}
            />
          </TabPanel>
        );
      })}
    </Styled.PaymentContainer>
  );
};

const TabPanel: FC<TabPanelProps> = ({
  hasMultiplePaymentMethods,
  isSelected,
  type,
  children,
}) => {
  if (!hasMultiplePaymentMethods) {
    return <>{children}</>;
  }

  return (
    <Styled.TabPanel
      $selected={isSelected}
      aria-labelledby={`payment-tab-${type}`}
      id={`payment-tabpanel-${type}`}
    >
      {children}
    </Styled.TabPanel>
  );
};

const PaymentMethodForm = ({
  paymentMethod,
  hasOwnUi,
  ...formProps
}: PaymentMethodFormProps) => {
  if (paymentMethod.type === PaymentMethodTypes.Paypal) {
    return <PaymentPayPalForm {...formProps} />;
  }

  if (paymentMethod.type === PaymentMethodTypes.CreditCard) {
    return hasOwnUi ? (
      <PaymentDropInForm {...formProps} />
    ) : (
      <PaymentCardForm {...formProps} />
    );
  }

  return null;
};
